/**
 * EasterEggOverlay — listens for /bomb, /walrus, /bucket events and
 * spawns a temporary sprite overlay over the chat area. Sprites are
 * the original SWF bitmaps (chid 269 / 425 / 255), copied verbatim.
 *
 * /bucket is the most-faithful of the three — it reproduces the
 * legacy SWF DefineSprite_436 ("walrus_bucket") animation:
 *
 *   1. The walrus-with-bucket image rises up from below the chat area
 *      (sprite 436 frame 2: `gotoAndStop("appear"); play()`).
 *   2. Sprite frame 7 fires `_root.genericMessageRecieved({m: "I has
 *      a bucket"})` — we delegate that to the existing mod-bucket chat
 *      line which the server already broadcasts.
 *   3. Sprite frames 8–~600 play the rising + hold portion alongside
 *      ~9s of embedded streaming MP3 audio (extracted via a custom
 *      SoundStreamBlock walker and trimmed to 10s).
 *   4. Sprite frame 599 stop() — walrus is fully visible. We mirror
 *      with a CSS hold.
 *   5. After ~9s total we slide the walrus back down.
 *
 * The audio file lives at /prochat/app/legacy/walrus-bucket.mp3 and
 * plays only if the user's `playSounds` pref is enabled (handled by
 * the audio's `muted` attribute synced on mount).
 */
import { useEffect, useRef, useState } from 'react';
import { loadPrefs } from '../lib/prefs.js';

type EffectKind = 'bomb' | 'walrus' | 'bucket';
interface Effect { id: number; kind: EffectKind; }

export interface EasterEggOverlayHandle {
    fire: (kind: EffectKind) => void;
}

interface Props {
    /** parent passes a ref so it can call .fire() when the WS events arrive */
    handleRef: React.MutableRefObject<EasterEggOverlayHandle | null>;
}

let nextId = 0;
/** TTL per effect kind — long enough for the longest stage of each anim. */
const TTL_MS: Record<'bomb' | 'walrus' | 'bucket', number> = {
    bomb:   3300,    // fall 1.4 + shake 1.0 + boom 0.8 + buffer
    walrus: 3000,
    bucket: 9500,    // faithful rise 1.2s + hold ~7s + descend 1s + buffer
};

/** Plays the bucket audio in sync with the on-screen walrus rise. */
function BucketAudio() {
    const ref = useRef<HTMLAudioElement | null>(null);
    useEffect(() => {
        const a = ref.current;
        if (!a) return;
        a.muted = !loadPrefs().playSounds;
        a.currentTime = 0;
        a.play().catch(() => { /* autoplay block — silent fallback */ });
    }, []);
    return (
        <audio ref={ref} src="/prochat/app/legacy/walrus-bucket.mp3" preload="auto" />
    );
}

export function EasterEggOverlay({ handleRef }: Props) {
    const [active, setActive] = useState<Effect[]>([]);
    const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

    useEffect(() => {
        handleRef.current = {
            fire: (kind: EffectKind) => {
                const id = ++nextId;
                setActive(s => [...s, { id, kind }]);
                const t = setTimeout(() => {
                    setActive(s => s.filter(e => e.id !== id));
                    timersRef.current.delete(id);
                }, TTL_MS[kind]);
                timersRef.current.set(id, t);
            },
        };
        return () => {
            for (const t of timersRef.current.values()) clearTimeout(t);
            timersRef.current.clear();
            handleRef.current = null;
        };
    }, [handleRef]);

    if (active.length === 0) return null;

    return (
        <div className="fx-layer" aria-hidden="true">
            {active.map(e => {
                if (e.kind === 'bomb') {
                    return <div key={e.id} className="fx-overlay fx-bomb" />;
                }
                if (e.kind === 'walrus') {
                    return (
                        <div key={e.id} className="fx-overlay fx-walrus">
                            <img src="/prochat/app/legacy/walrus-tusks.png" alt="" />
                        </div>
                    );
                }
                // bucket — faithful SWF chid 436 reproduction with audio
                return (
                    <div key={e.id} className="fx-overlay fx-bucket">
                        <img src="/prochat/app/legacy/walrus-with-cookie.png" alt="" />
                        <BucketAudio />
                    </div>
                );
            })}
        </div>
    );
}
